/*
 * Copyright (c) Nexign, JSC, 2020.
 */
/**
 * Table component for Settings statistics
 *
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @version 1.10.0
 * @since 1.3.1
 */

import { useEffect } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { Table } from '../../../../../../components/common';
import { loadTableFieldsSuccessful, loadTableListSuccessful } from '../../../../../../components/Table/actions/tableActions';
import { ITableField, ITableReducer } from '../../../../../../components/Table/reducers/tableReducer';
import {
    makeSelectorTableData,
    makeSelectorTableFields,
    makeSelectorTableIsLoading,
    makeSelectorTableSort,
} from '../../../../../../components/Table/selectors/tableSelectors';
import TableColumnUtil from '../../../../../../components/Table/TableColumnUtil';
import {
    ILoadTableFieldsSuccessfulPayload,
    ILoadTableListSuccessfulPayload,
    TableNameType,
} from '../../../../../../components/Table/types/tableTypes';
import {
    makeSelectPageSettingsStatisticsTranslation,
    makeSelectTranslationCommon,
} from '../../../../../../modules/internationalization/intSelectors';
import { ICommonTranslation, ICorporateTranslation } from '../../../../../../modules/internationalization/types';
import { IPageSettingsStatisticsTranslation } from '../../../../../../modules/internationalization/types/pages/pageSettings';
import { guardComponentType } from '../../../../../../modules/role/reducer/guardReducer';
import { makeSelectGuardComponents } from '../../../../../../modules/role/selectors/guardSelector';
import { makeSelectIsDensity } from '../../../../../../modules/storage/storageSelectors';
import { IRootReducer } from '../../../../../../reducers/rootReducer';
import {
    formStatisticsTableFields,
    getSettingsStatisticsColumnCellType,
    getSettingsStatisticsColumnFilterType,
    SettingsSettingsCellConfig,
} from './settingsStatisticsTableCells';
import { ESettingsStatisticsField, SettingsStatisticsColumnNames } from './settingsStatisticsTableTypes';

interface ISettingsStatisticsProps {
    tableCount: number;
}

type TSettingsAutoReportsProps = ISettingsStatisticsProps & IConnectedProps & IDispatchProps;

const SettingsStatisticsTable = (props: TSettingsAutoReportsProps) => {
    const tableColumnUtil = new TableColumnUtil<ESettingsStatisticsField,
        IPageSettingsStatisticsTranslation & ICorporateTranslation & ICommonTranslation>(
        {
            cellConfig: SettingsSettingsCellConfig,
            columnNames: SettingsStatisticsColumnNames,
            componentsFeature: props.componentsFeature,
            fields: props.fields as Array<ITableField<ESettingsStatisticsField>>,
            getColumnCellType: getSettingsStatisticsColumnCellType,
            getColumnFilterType: getSettingsStatisticsColumnFilterType,
            getColumnFixedType: () => false,
            isTableResizeEnabled: false,
            isDisableFilter: true,
            isDisableSort: true,
            translation: {
                ...props.commonTranslation,
                ...props.settingsStatisticsTranslation,
            },
        });

    useEffect(() => {
        props.loadTableFieldsSuccessful({
            fields: formStatisticsTableFields(props.settingsStatisticsTranslation.table),
        })
    }, [props.settingsStatisticsTranslation.table]);

    return (
        <Table
            basketAllIn={false}
            columns={tableColumnUtil.customizeTableColumns()}
            counter={props.tableCount}
            data={props.data}
            fields={props.fields}
            isDensity={props.isDensity}
            isLoading={props.isLoading}
            loadTableList={() => {
            }}
            saveResizedChange={() => {
            }}
            setTableSort={() => {
            }}
            sideBarWidth={264}
            sort={props.sort}
        />
    )
}

interface IConnectedProps {
    commonTranslation: ICommonTranslation;
    componentsFeature: guardComponentType;
    data: ITableReducer[TableNameType]['data'];
    fields: ITableReducer[TableNameType]['fields'];
    isDensity: boolean;
    isLoading: boolean;
    settingsStatisticsTranslation: IPageSettingsStatisticsTranslation;
    sort: ITableReducer[TableNameType]['sort'];
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    commonTranslation: makeSelectTranslationCommon(),
    componentsFeature: makeSelectGuardComponents(),
    data: makeSelectorTableData(TableNameType.settingsStatistics),
    fields: makeSelectorTableFields(TableNameType.settingsStatistics),
    isDensity: makeSelectIsDensity(),
    isLoading: makeSelectorTableIsLoading(TableNameType.settingsStatistics),
    settingsStatisticsTranslation: makeSelectPageSettingsStatisticsTranslation(),
    sort: makeSelectorTableSort(TableNameType.settingsStatistics),
});

interface IDispatchProps {
    loadTableFieldsSuccessful: (payload: ILoadTableFieldsSuccessfulPayload) => void;
    loadTableListSuccessful: (payload: ILoadTableListSuccessfulPayload) => void;
}

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    loadTableFieldsSuccessful: (payload) => dispatch(loadTableFieldsSuccessful(payload)),
    loadTableListSuccessful: (payload) => dispatch(loadTableListSuccessful(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SettingsStatisticsTable);
